import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * CaseFinancials
 *
 * A compact strip that sits at the top of the project detail view. Lets
 * the attorney enter Settlement / Attorney Fees % / Legal Costs once per
 * project, then auto-fills those values into every negotiation form on
 * this case.
 *
 * Save behavior:
 *   - On blur, if the value differs from the last saved value, PATCH
 *     /api/projects/{id}/case-settings with the changed field.
 *   - Brief "✓ Saved" indicator flashes on the changed field to confirm.
 *
 * Props:
 *   projectId       string — the Filevine project id
 *   initialValues   object — { settlement_amount, attorney_fees_pct,
 *                              legal_costs, settlement_confidential }
 *                            from the project detail response
 *   onSaved         fn(newValues) — called after any successful save so
 *                                    the parent can update local state
 *                                    (e.g., so MayLogicScratchpad reflects
 *                                    the change immediately)
 */
function CaseFinancials({ projectId, initialValues, onSaved }) {
  const [settlement, setSettlement] = useState(initialValues?.settlement_amount ?? '');
  const [feesPct, setFeesPct]       = useState(initialValues?.attorney_fees_pct ?? 33);
  const [legalCosts, setLegalCosts] = useState(initialValues?.legal_costs ?? '');
  const [savedIndicator, setSavedIndicator] = useState(null);
  const savedTimersRef = useRef({});

  // Keep local state in sync if the parent's initialValues change (e.g.,
  // navigating between projects).
  useEffect(() => {
    setSettlement(initialValues?.settlement_amount ?? '');
    setFeesPct(initialValues?.attorney_fees_pct ?? 33);
    setLegalCosts(initialValues?.legal_costs ?? '');
  }, [initialValues?.settlement_amount, initialValues?.attorney_fees_pct, initialValues?.legal_costs, projectId]);

  const flashSaved = (field) => {
    setSavedIndicator(field);
    if (savedTimersRef.current[field]) clearTimeout(savedTimersRef.current[field]);
    savedTimersRef.current[field] = setTimeout(() => {
      setSavedIndicator((s) => (s === field ? null : s));
    }, 2500);
  };

  const saveField = async (field, value) => {
    const body = { [field]: value };
    try {
      const res = await axios.patch(
        `${API_URL}/api/projects/${projectId}/case-settings`,
        body,
        { withCredentials: true }
      );
      flashSaved(field);
      if (onSaved) onSaved(res.data);
    } catch (e) {
      console.warn('Failed to save case setting', field, e);
    }
  };

  const handleBlur = (field, newValue, oldValue) => {
    const parsed = newValue === '' ? null : parseFloat(newValue);
    const prev = oldValue === '' ? null : parseFloat(oldValue);
    if (parsed === prev) return; // No change, no save
    saveField(field, parsed);
  };

  // Compute Pre-Lien Net for the little live-stat card
  const settlementNum = parseFloat(settlement) || 0;
  const feesPctNum = parseFloat(feesPct) || 0;
  const costsNum = parseFloat(legalCosts) || 0;
  const preLienNet = settlementNum - settlementNum * (feesPctNum / 100) - costsNum;
  const fmt = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

  return (
    <div style={styles.wrap}>
      <div style={styles.grid}>
        <div style={styles.brandCol}>
          <div style={styles.eyebrow}>CASE FINANCIALS</div>
          <div style={styles.tagline}>Enter once — auto-fills all negotiations</div>
        </div>

        <FieldCol
          label="Settlement"
          value={settlement}
          onChange={setSettlement}
          onBlur={(v) => handleBlur('settlement_amount', v, initialValues?.settlement_amount)}
          prefix="$"
          saved={savedIndicator === 'settlement_amount'}
        />
        <FieldCol
          label="Att. Fees"
          value={feesPct}
          onChange={setFeesPct}
          onBlur={(v) => handleBlur('attorney_fees_pct', v, initialValues?.attorney_fees_pct)}
          suffix="%"
          saved={savedIndicator === 'attorney_fees_pct'}
        />
        <FieldCol
          label="Costs"
          value={legalCosts}
          onChange={setLegalCosts}
          onBlur={(v) => handleBlur('legal_costs', v, initialValues?.legal_costs)}
          prefix="$"
          saved={savedIndicator === 'legal_costs'}
        />

        <div style={styles.statCol}>
          <div style={styles.statLabel}>Pre-Lien Net</div>
          <div style={styles.statValue}>
            {settlementNum > 0 ? fmt(preLienNet) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldCol({ label, value, onChange, onBlur, prefix, suffix, saved }) {
  return (
    <div style={styles.fieldCol}>
      <div style={styles.fieldLabelRow}>
        <span style={styles.fieldLabel}>{label}</span>
        {saved && <span style={styles.savedBadge}>✓ Saved</span>}
      </div>
      <div style={styles.fieldInputWrap}>
        {prefix && <span style={styles.prefix}>{prefix}</span>}
        <input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onBlur(e.target.value)}
          style={styles.input}
        />
        {suffix && <span style={styles.suffix}>{suffix}</span>}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '14px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(3, 1fr) 1.4fr',
    gap: 16,
    alignItems: 'end',
  },
  brandCol: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  eyebrow: {
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontSize: 10,
    letterSpacing: 1.5,
    color: '#A1B808',
  },
  tagline: { fontSize: 11, color: '#6b7585', marginTop: 2 },
  fieldCol: { display: 'flex', flexDirection: 'column', position: 'relative' },
  fieldLabelRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
    height: 14,             // fixed height so the badge appearing doesn't shift layout
  },
  fieldLabel: {
    fontSize: 10,
    color: '#6b7585',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
  },
  // Badge is absolutely positioned to the top-right of the fieldCol — it
  // floats above the input rather than sharing flow with the label. This
  // is what prevents the "meds tab bottom jumps" issue: any layout shift
  // above the meds table (e.g., save indicator appearing/disappearing)
  // would shift everything below it. Absolute position keeps flow stable.
  savedBadge: {
    position: 'absolute',
    top: -2,
    right: 0,
    fontSize: 10,
    fontWeight: 700,
    color: '#A1B808',
    textTransform: 'none',
    letterSpacing: 0.2,
    pointerEvents: 'none',
  },
  fieldInputWrap: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    padding: '6px 10px',
    background: 'white',
  },
  prefix: { color: '#6b7585', marginRight: 4, fontSize: 13 },
  suffix: { color: '#6b7585', marginLeft: 4, fontSize: 13 },
  input: {
    border: 0,
    outline: 'none',
    flex: 1,
    fontSize: 13,
    color: '#171D29',
    minWidth: 0,
    fontFamily: 'inherit',
  },
  statCol: {
    background: 'rgba(161,184,8,0.10)',
    borderLeft: '3px solid #A1B808',
    padding: '8px 12px',
    borderRadius: 4,
  },
  statLabel: {
    fontSize: 10,
    color: '#4F5A04',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 700,
  },
  statValue: {
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontSize: 16,
    color: '#171D29',
  },
};

export default CaseFinancials;
